// The rack of game cases holds as many as the room has slots for. More games
// than that are on further pages of the same rack: the built-in games first,
// then the visitor's own, then what other people published, each group in
// the order the snapshot gives it. A page is a run of that order one rack
// long, so a game's page changes only when a game before it comes or goes.

import type { AppInfo } from "./types.ts";

const RANK: Record<AppInfo["origin"], number> = { starter: 0, mine: 1, shared: 2 };

/** The games in the order the rack shows them. The sort is stable. */
export function shelfOrder(apps: readonly AppInfo[]): AppInfo[] {
  return [...apps].sort((a, b) => RANK[a.origin] - RANK[b.origin]);
}

/** How many pages `total` games take on a rack of `size` slots: one at least, so an empty rack still has a page. */
export function pageCount(total: number, size: number): number {
  return Math.max(1, Math.ceil(total / Math.max(1, size)));
}

/** The games on one page, in slot order. */
export function pageSlice<T>(ordered: readonly T[], page: number, size: number): T[] {
  return page < 0 ? [] : ordered.slice(page * size, (page + 1) * size);
}

/** The page a game is on, or -1 when the rack does not hold it. */
export function pageOf(ordered: readonly AppInfo[], id: string, size: number): number {
  const at = ordered.findIndex((app) => app.id === id);
  return at < 0 ? -1 : Math.floor(at / Math.max(1, size));
}

/** A step along the rack from a game: to the one before or after it, or to the one above or below it. */
export type ShelfStep = "previous" | "next" | "up" | "down";

/** Where a slot of the rack stands: how far along the rack, and how high. */
export interface SlotPlace {
  along: number;
  up: number;
}

/** Two slots closer than this, in metres, stand in one row. */
const ROW = 1e-3;

/**
 * The game a step from `id` lands on, or null when there is none. Before and
 * after are the rack's order, across its pages. Above and below are on the
 * game's own page: the nearest row of games higher or lower than its slot,
 * and in that row the game nearest along the rack. `slots` are the rack's
 * slots in the order a page fills them. A rack of one row has nothing above
 * or below.
 */
export function shelfStep(ordered: readonly AppInfo[], id: string, step: ShelfStep, slots: readonly SlotPlace[]): string | null {
  const at = ordered.findIndex((app) => app.id === id);
  if (at < 0) return null;
  if (step === "previous") return ordered[at - 1]?.id ?? null;
  if (step === "next") return ordered[at + 1]?.id ?? null;
  const size = Math.max(1, slots.length);
  const first = at - (at % size);
  const here = slots[at - first];
  if (!here) return null;
  const sign = step === "up" ? 1 : -1;
  let best: { index: number; rise: number; off: number } | null = null;
  for (let slot = 0; slot < size && first + slot < ordered.length; slot++) {
    const place = slots[slot]!;
    const rise = (place.up - here.up) * sign;
    if (rise <= ROW) continue;
    const off = Math.abs(place.along - here.along);
    const nearer = !best || rise < best.rise - ROW || (Math.abs(rise - best.rise) <= ROW && off < best.off);
    if (nearer) best = { index: first + slot, rise, off };
  }
  return best ? ordered[best.index]!.id : null;
}
